/*
 *  Copyright (c) 2024. Rapida
 *
 *  Permission is hereby granted, free of charge, to any person obtaining a copy
 *  of this software and associated documentation files (the "Software"), to deal
 *  in the Software without restriction, including without limitation the rights
 *  to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 *  copies of the Software, and to permit persons to whom the Software is
 *  furnished to do so, subject to the following conditions:
 *
 *  The above copyright notice and this permission notice shall be included in
 *  all copies or substantial portions of the Software.
 *
 *  THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 *  IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 *  FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 *  AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 *  LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 *  OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
 *  THE SOFTWARE.
 *
 *  Author: Prashant <prashant@rapida.ai>
 */

import {
  AgentCallback,
  ConversationAssistantMessage as AssistantMessageWrapper,
  ConversationUserMessage as UserMessageWrapper,
} from "@/rapida/types/agent-callback";
import {
  WebTalkResponse,
  ServerSignaling,
  WebRTCSDP,
} from "@/rapida/clients/protos/webrtc_pb";
import type { GrpcSignalingManager } from "./grpc-signaling-manager";
import type { WebRTCPeerManager } from "./webrtc-peer-manager";
import type { AudioMediaManager } from "./audio-media-manager";

/**
 * MessageProtocolHandler — Dispatches incoming WebTalkResponse messages
 *
 * Responsibilities:
 * - Parse each response and route to the correct manager / callback
 * - Handle ServerSignaling (config, SDP, ICE, ready, clear, error)
 * - Handle conversation init/config responses
 * - Handle assistant / user / interruption / tool call messages
 *
 * State: tracks the active WebRTC signaling session to ignore stale offers/ICE.
 */
export class MessageProtocolHandler {
  // Serialization chain ensures signaling messages (CONFIG → OFFER → ICE)
  // are processed in order even though gRPC callbacks fire without await.
  private processingChain: Promise<void> = Promise.resolve();
  private activeSignalingSessionId: string | null = null;

  constructor(
    private callbacks: AgentCallback,
    private signaling: GrpcSignalingManager,
    private peer: WebRTCPeerManager,
    private audio: AudioMediaManager,
  ) { }

  // ---------------------------------------------------------------------------
  // Top-level dispatch
  // ---------------------------------------------------------------------------

  async handleMessage(response: WebTalkResponse): Promise<void> {
    this.processingChain = this.processingChain
      .then(() => this._handleMessage(response))
      .catch(err => console.error("[Protocol] Message processing error", err));
    return this.processingChain;
  }

  private async _handleMessage(response: WebTalkResponse): Promise<void> {
    // Server signaling (SDP, ICE, config, ready, clear, error)
    if (response.hasSignaling()) {
      const sig = response.getSignaling();
      if (sig) await this.handleServerSignaling(sig);
    }

    // Conversation initialization (conversation ID, etc.)
    if (response.hasInitialization()) {
      const init = response.getInitialization();
      if (init) {
        const convId = init.getAssistantconversationid();
        if (convId) this.signaling.setConversationId(convId);
        this.callbacks.onInitialization?.(init.toObject());
      }
    }

    // Conversation configuration (mode switch acknowledgment)
    if (response.hasConfiguration()) {
      const cfg = response.getConfiguration();
      if (cfg) this.callbacks.onConfiguration?.(cfg.toObject());
    }

    // Assistant message
    if (response.hasAssistant()) {
      const assistant = response.getAssistant();
      if (assistant) {
        this.callbacks.onAssistantMessage?.(new AssistantMessageWrapper(assistant));
      }
    }

    // User message echo
    if (response.hasUser()) {
      const user = response.getUser();
      if (user) {
        this.callbacks.onUserMessage?.(new UserMessageWrapper(user));
      }
    }

    // Interruption
    if (response.hasInterruption()) {
      const interruption = response.getInterruption();
      if (interruption) this.callbacks.onInterrupt?.(interruption.toObject());
    }

    // Tool call (server invokes a tool — client may need to act)
    if (response.hasToolcall()) {
      const toolCall = response.getToolcall();
      if (toolCall) this.callbacks.onToolCall?.(toolCall.toObject());
    }

    // Tool call result (server-side tool completed — informational)
    if (response.hasToolcallresult()) {
      const result = response.getToolcallresult();
      if (result) this.callbacks.onToolCallResult?.(result.toObject());
    }

    // Disconnection (server requests disconnect)
    if (response.hasDisconnection()) {
      const disconnection = response.getDisconnection();
      await this.callbacks.onConversationDisconnected?.(disconnection?.toObject());
      this.callbacks.onDisconnected?.();
    }

    // Pipeline conversation event (STT, TTS, LLM, session, etc.)
    if (response.hasEvent()) {
      const event = response.getEvent();
      if (event) this.callbacks.onConversationEvent?.(event.toObject());
    }

    // Conversation error (server-side error with conversation ID and details)
    if (response.hasError()) {
      const error = response.getError();
      if (error) {
        this.callbacks.onError?.(new Error(error.getMessage()));
        this.callbacks.onConversationError?.(error.toObject());
      }
    }

    // Metric (server-side performance/latency data)
    if (response.hasMetric()) {
      const metric = response.getMetric();
      if (metric) this.callbacks.onMetric?.(metric.toObject());
    }
  }

  // ---------------------------------------------------------------------------
  // Server signaling
  // ---------------------------------------------------------------------------

  private async handleServerSignaling(signaling: ServerSignaling): Promise<void> {
    const signalingSessionId = signaling.getSessionid();
    const messageCase = signaling.getMessageCase();

    if (messageCase !== ServerSignaling.MessageCase.CONFIG) {
      if (this.activeSignalingSessionId && signalingSessionId && signalingSessionId !== this.activeSignalingSessionId) {
        console.debug("[Protocol] Ignoring stale WebRTC signaling", {
          activeSignalingSessionId: this.activeSignalingSessionId,
          signalingSessionId,
          messageCase,
        });
        return;
      }
      if (!this.activeSignalingSessionId && signalingSessionId) {
        this.activeSignalingSessionId = signalingSessionId;
        this.signaling.setSessionId(signalingSessionId);
      }
    }

    switch (messageCase) {
      case ServerSignaling.MessageCase.CONFIG: {
        try {
          this.activeSignalingSessionId = signalingSessionId || null;
          if (this.activeSignalingSessionId) {
            this.signaling.setSessionId(this.activeSignalingSessionId);
          }

          const config = signaling.getConfig();
          const iceServers = config?.getIceserversList()?.map(srv => ({
            urls: srv.getUrlsList(),
            username: srv.getUsername() || undefined,
            credential: srv.getCredential() || undefined,
          })) as RTCIceServer[] | undefined;
          this.peer.setup(iceServers);
        } catch (error) {
          this.peer.close();
          console.error("Failed to handle WebRTC config signaling", error);
          this.callbacks.onConnectionStateChange?.("failed");
          this.callbacks.onError?.(new Error(`WebRTC config signaling failed: ${error}`));
        }
        break;
      }

      case ServerSignaling.MessageCase.SDP: {
        const sdpMessage = signaling.getSdp();
        const sdpType = sdpMessage?.getType();
        try {
          if (!sdpMessage) break;

          const sdp = sdpMessage.getSdp();

          if (sdpType === WebRTCSDP.SDPType.OFFER) {
            // console.log("[Protocol] Handling SDP OFFER");
            const answerSdp = await this.peer.handleOffer(sdp, this.audio.mediaStream);
            if (answerSdp) {
              this.signaling.sendWebRTCAnswer(answerSdp);
            } else {
              console.debug("[Protocol] SDP offer was superseded before answer was sent");
            }
          } else if (sdpType === WebRTCSDP.SDPType.ANSWER) {
            // console.log("[Protocol] Handling SDP ANSWER");
            await this.peer.handleAnswer(sdp);
          }
        } catch (error) {
          if (sdpType === WebRTCSDP.SDPType.OFFER) {
            // Cleanup strategy: on failed offer handling, close stale peer state.
            // Next CONFIG/OFFER cycle will recreate the peer cleanly.
            this.peer.close();
          }
          console.error("Failed to handle SDP signaling", error);
          this.callbacks.onConnectionStateChange?.("failed");
          this.callbacks.onError?.(new Error(`SDP signaling failed: ${error}`));
        }
        break;
      }

      case ServerSignaling.MessageCase.ICECANDIDATE: {
        try {
          const ice = signaling.getIcecandidate();
          if (ice && ice.getCandidate()) {
            await this.peer.addICECandidate(
              ice.getCandidate(),
              ice.getSdpmid(),
              ice.getSdpmlineindex(),
            );
          }
        } catch (error) {
          console.debug("Failed to add ICE candidate (non-fatal)", error);
        }
        break;
      }

      case ServerSignaling.MessageCase.READY:
        try {
          await this.audio.ensurePlayback();
        } catch (error) {
          console.error("Failed to ensure audio playback", error);
          this.callbacks.onError?.(new Error(`Audio playback setup failed: ${error}`));
        }
        break;

      case ServerSignaling.MessageCase.CLEAR:
        this.callbacks.onInterrupt?.(undefined);
        break;

      case ServerSignaling.MessageCase.ERROR: {
        const errorMsg = signaling.getError();
        this.callbacks.onError?.(new Error(errorMsg || "Signaling error"));
        break;
      }
    }
  }
}
